import { when } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as moment from 'moment';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { FRESHDESK, PhotoTabConstants as ui } from '../../Config/UIConstants';
import { EmptyContentOnTabDisplay } from '../../SharedComponents/EmptyContentOnTabDisplay';
import { ExitableAlert } from '../../SharedComponents/ExitableAlert';
import { FullContainer } from '../../SharedComponents/FullContainer';
import { CollectionGrid } from '../../SharedComponents/GridDisplay/CollectionGrid';
import { ActiveTabState, NavigationContainer } from '../../SharedComponents/MainPageWithNavigator/MainPageWithNavigator';
import { ModalActionButton } from '../../SharedComponents/ModalActionButton';
import { MultiSelectSinglePhotoDisplay } from '../../SharedComponents/MultiSelectSinglePhotoDisplay';
import { SortDropdownContained } from '../../SharedComponents/SortDropdownContained';
import { IGetAllPhotosForCurrentUserMeta, IUserPhoto } from '../../Stores/UserPhotoStore';
import { Routes } from '../../_controllers/RouteController';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import { colors } from '../../styles/colors';

enum OrderBy {
    CreatedNewestFirst = 'Newest',
    CreatedOldestFirst = 'Oldest'
}

export interface IProps {
    store: ApplicationStore;
}

interface IState {
    orderBy: OrderBy;
    selectedPhotosByID: Array<number>;
    deleteResponse: string;
}

@inject('store')
@observer
export class PhotosPage extends React.Component<IProps & RouteComponentProps<any>, IState> {
    public componentDidMount(): void {

        when(
            () => !!this.props.store.userPhotoStore,
            async () => {
                if (this.props.store.userPhotoStore && !this.props.store.userPhotoStore.initialPhotosAreLoaded) {
                    console.log('fetching for the first time');
                    await this.props.store.userPhotoStore.getAllPhotosFromServer();
                }
                let scrollTo = 0;

                if (this.props.store.userPhotoStore.photosPageScrollTo) {
                    scrollTo = this.props.store.userPhotoStore.photosPageScrollTo;
                }
                window.setTimeout(() => {
                    window.scrollTo(0, scrollTo);
                }, 100);
            }
        );
    }

    public componentWillUnmount(): void {
        this.props.store.userPhotoStore.setPhotosPageScrollTo(window.pageYOffset);
    }

    private fetchPhotos = async (hideSpinner: boolean) => {
        if (!this.props.store.userPhotoStore.loadingPhotos) {
            await this.props.store.userPhotoStore.getAllPhotosFromServer(hideSpinner);
        } else {
            console.log('already loading');
        }
    }

    public state: IState = {
        orderBy: OrderBy.CreatedNewestFirst,
        selectedPhotosByID: [],
        deleteResponse: ''
    };

    private comparePhotosForSort(): (a: IUserPhoto, b: IUserPhoto) => number {
        switch (this.state.orderBy) {
            case OrderBy.CreatedNewestFirst:
                return (a, b) => moment(b.created_at).diff(moment(a.created_at));
            case OrderBy.CreatedOldestFirst:
                return (a, b) => moment(a.created_at).diff(moment(b.created_at));
            default:
                throw new Error(`Invalid sort order: ${this.state.orderBy}`);
        }
    }

    private renderItem = (photo: IUserPhoto) => {
        const photoIsSelected = this.state.selectedPhotosByID.some(id => id === photo.id);

        return (
            <FullContainer style={styles.pointerCursor}>
                <MultiSelectSinglePhotoDisplay
                    url={photo.thumbnail_url || photo.url}
                    onClickPhoto={
                        this.state.selectedPhotosByID.length === 0 ? () => this.props.history.push(`/photodetail/${photo.id}`, { scrollTo: window.pageYOffset }) : () => null}

                    onClickCheckMark={() => {
                        const selectedPhotosByID = [...this.state.selectedPhotosByID];
                        const foundIndex = selectedPhotosByID.findIndex(id => id === photo.id);
                        if (foundIndex >= 0) {
                            selectedPhotosByID.splice(foundIndex, 1);
                        } else {
                            selectedPhotosByID.push(photo.id);
                        }
                        this.setState({ selectedPhotosByID });
                    }}
                    photoIsSelected={photoIsSelected}
                    photoHeight={250}
                    isFullWidth={true}
                    overrideHover={this.state.selectedPhotosByID.length !== 0}
                />
            </FullContainer>
        );
    }

    private goToCreateStoryWithPhotos(): void {
        this.props.history.push(Routes.ADD_STORY, { photoIDsForStory: this.state.selectedPhotosByID });
    }

    private handleOrderByChange = (orderBy: OrderBy) => {
        this.setState({ orderBy: orderBy as OrderBy });
        const {getAllPhotosForCurrentUserMeta, setGetAllPhotosForCurrentUserMeta} = this.props.store.userPhotoStore;
        let newMeta = {
            page: 0,
        } as IGetAllPhotosForCurrentUserMeta;
        if (orderBy === OrderBy.CreatedNewestFirst) {
            newMeta.sort = 'DESC';
        } else {
            newMeta.sort = 'ASC';
        }
        this.props.store.userPhotoStore.setPhotosPageSort(orderBy);
        this.props.store.userPhotoStore.setGetAllPhotosForCurrentUserMeta(newMeta);
    }

    private loadMore = () => {
        if (this.props.store) {
            const {loadingPhotos, allPhotosLoaded} = this.props.store.userPhotoStore;
            if (!loadingPhotos && !allPhotosLoaded) {
                this.fetchPhotos(true);
            }
        }
    }

    private renderLoader = () => {
        return (
            <FullContainer key={0} style={{...styles.loadingBackground, paddingTop: 10}}>
                <p style={styles.loadingFont}>Loading More...</p>
            </FullContainer>
        );
    }

    private hasMore = () => {
        return this.props.store && !this.props.store.userPhotoStore.allPhotosLoaded;
    }

    public render() {
        if (
            !this.props.store.authorizedStoresAreLoaded ||
          !this.props.store.storyStore ||
            !this.props.store.userStore.currentUser
        ) {
            return (
                <>
                    <NavigationContainer {...this.props} activeTabState={ActiveTabState.PHOTOS} />;
                </>
            );
        }


        const currentUser = this.props.store.userStore.currentUser;
        const photoContent = [...this.props.store.userPhotoStore.userPhotos].filter(p => p.user_id === currentUser.id);
        // const sortedPhotos = photoContent.sort(this.comparePhotosForSort());
        const sortedPhotos = photoContent;


        return (
            <NavigationContainer
                {...this.props}
                activeTabState={ActiveTabState.PHOTOS}
                title={ui.YOUR_PHOTOS}
                titleInfoIconHref={FRESHDESK.YOUR_PHOTOS}
                primaryButton={{ text: ui.ADD_PHOTOS, action: () => this.props.history.push(Routes.ADD_PHOTOS) }}
            >
                <FullContainer style={styles.relative}>

                    {this.props.store.userPhotoStore.loadingPhotos ? (
                        <FullContainer style={styles.loadingBackground}>
                            <p style={styles.loadingFont}>Loading Photos...</p>
                        </FullContainer>
                    ) : (
                        <>
                            {this.props.store.userPhotoStore.loadingPhotosError ? (
                                <FullContainer style={styles.loadingBackground}>
                                    <p style={styles.loadingFont}>
                                        There was a problem loading your photos. Please&nbsp;
                                        <a
                                            href="/"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                this.props.store.userPhotoStore.getAllPhotosFromServer();
                                            }}
                                        >
                                            try again
                                        </a>.
                                    </p>

                                    <p>
                                        If the problem persists, please <a href="https://tightknit.freshdesk.com/support/tickets/new" target="_blank" rel="noopener">contact support</a>.
                                    </p>
                                </FullContainer>

                            ) : (
                                <>
                                    {photoContent.length === 0 ? (
                                        <EmptyContentOnTabDisplay
                                            header={ui.NO_PHOTOS}
                                            subHeader={ui.ADD_SOME_PHOTOS}
                                            buttonText={ui.ADD_PHOTOS}
                                            onPress={() => this.props.history.push(Routes.ADD_PHOTOS)}
                                        />
                                    ) : (
                                        <FullContainer>
                                            <SortDropdownContained
                                                options={[OrderBy.CreatedNewestFirst, OrderBy.CreatedOldestFirst]}
                                                value={this.props.store.userPhotoStore.photosPageSort}
                                                onChange={this.handleOrderByChange}
                                            />

                                            <CollectionGrid
                                                keyExtractor={(photo: IUserPhoto) => photo.id}
                                                content={sortedPhotos}
                                                renderItem={this.renderItem}
                                                isSquareContent={true}
                                                bottomPadding={200}
                                                infiniteScroll={true}
                                                infiniteLoader={this.renderLoader()}
                                                infiniteloadMore={this.loadMore}
                                                infinitehasMore={this.hasMore()}
                                            />

                                        </FullContainer>
                                    )}
                                </>
                            )}
                        </>
                    )}


                    {this.state.selectedPhotosByID.length !== 0 && (
                        <div style={styles.bottomButtonRow}>
                            <ModalActionButton isCancel={true} buttonText={ui.DESELECT_ALL} onClick={() => this.setState({ selectedPhotosByID: [] })} />
                            <div style={styles.widthBuffer} />
                            <ModalActionButton buttonText={ui.CREATE_ALBUM} onClick={() => this.goToCreateStoryWithPhotos()} />
                        </div>
                    )}

                    {this.state.deleteResponse !== '' && (
                        <ExitableAlert
                            headerText={this.state.deleteResponse}
                            confirmText={this.state.deleteResponse === ui.DELETE_IN_PROGRESS ? undefined : 'Ok'}
                            onConfirmClick={() => this.setState({ deleteResponse: '' })}
                            isFixed={true}
                        />
                    )}
                </FullContainer>
            </NavigationContainer>
        );
    }
}

export const styles: {[key: string]: React.CSSProperties} = {
    widthBuffer: {
        width: 20
    },
    cancelBackground: {
        backgroundColor: colors.CANCEL_TEXT
    },
    smallRightMargin: {
        marginRight: 25
    },
    bottomButtonRow: {
        height: 100,
        width: '100%',
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: colors.LIGHT_GREY,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingRight: 30
    },
    relative: {
        position: 'relative'
    },
    photoContainer: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        objectPosition: 'top'
    },
    pointerCursor: {
        cursor: 'pointer'
    },
    loadingFont: {
        fontSize: 20,
        marginBottom: 10,
    },
    loadingBackground: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: 200,
        paddingBottom: 100,
        flexDirection: 'column',
    } as React.CSSProperties,
};



// WEBPACK FOOTER //
// ./src/Pages/PhotosPage/PhotosPage.tsx